import { useState, useEffect, useMemo, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Calendar as CalIcon, Users, Plus, X, Share2, Copy, Trash2,
  ChevronLeft, ChevronRight, MapPin, Phone, Mail, Link as LinkIcon,
  Pencil, Check, Heart, PartyPopper, FileSignature, Send
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const EVENT_TYPES = [
  { key: "wedding",  label: "Wedding",       Icon: Heart,        accent: "rose" },
  { key: "prom",     label: "Prom",          Icon: Sparkles,     accent: "violet" },
  { key: "photo",    label: "Photo Shoot",   Icon: CalIcon,      accent: "sky" },
  { key: "corporate",label: "Corporate",     Icon: Users,        accent: "slate" },
  { key: "party",    label: "Group / Party", Icon: PartyPopper,  accent: "amber" },
  { key: "other",    label: "Other",         Icon: Sparkles,     accent: "teal" },
];
const STATUSES = [
  { key: "inquiry",   label: "Inquiry",   color: "bg-amber-50 text-amber-700" },
  { key: "booked",    label: "Booked",    color: "bg-emerald-50 text-emerald-700" },
  { key: "completed", label: "Completed", color: "bg-slate-100 text-slate-600" },
  { key: "cancelled", label: "Cancelled", color: "bg-rose-50 text-rose-700" },
];

const typeMeta = (key) => EVENT_TYPES.find(t => t.key === key) || EVENT_TYPES[0];
const statusMeta = (key) => STATUSES.find(s => s.key === key) || STATUSES[0];

const fmtDateLong = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("en-US",
      { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};
const fmtDateShort = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("en-US",
      { month: "short", day: "numeric" });
  } catch { return iso; }
};
const todayISO = () => new Date().toISOString().slice(0, 10);

// ─── Mini Calendar ─────────────────────────────────────────────
function MiniCalendar({ events, onPickDate, selectedDate }) {
  const [cursor, setCursor] = useState(() => new Date());
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthLabel = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDay; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const dateString = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const eventsByDate = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      if (!ev.date) continue;
      const arr = map.get(ev.date) || [];
      arr.push(ev);
      map.set(ev.date, arr);
    }
    return map;
  }, [events]);

  const today = todayISO();

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="events-mini-calendar">
      <div className="flex items-center justify-between mb-4">
        <p className="font-['Outfit'] text-base font-semibold text-slate-900">{monthLabel}</p>
        <div className="flex items-center gap-1">
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="calendar-prev"
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <ChevronLeft className="w-4 h-4 text-slate-600" />
          </button>
          <button onClick={() => setCursor(new Date())} className="text-[11px] px-2 h-7 rounded-full hover:bg-slate-100 text-slate-600">Today</button>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="calendar-next"
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <ChevronRight className="w-4 h-4 text-slate-600" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={`${d}-${i}`} className="text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={`pad-${i}`} />;
          const ds = dateString(d);
          const evs = eventsByDate.get(ds) || [];
          const isSelected = selectedDate === ds;
          const isToday = ds === today;
          return (
            <button key={ds} onClick={() => onPickDate?.(ds)} data-testid={`day-${ds}`}
              className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm relative transition-colors ${
                isSelected ? "bg-slate-900 text-white"
                  : isToday ? "bg-rose-50 text-rose-700 font-semibold ring-1 ring-rose-200"
                  : evs.length ? "bg-rose-50 hover:bg-rose-100 text-slate-700"
                  : "hover:bg-slate-100 text-slate-600"
              }`}>
              <span>{d}</span>
              {evs.length > 0 && !isSelected && (
                <span className="absolute bottom-1 w-1 h-1 rounded-full bg-rose-500" />
              )}
              {evs.length > 0 && isSelected && (
                <span className="absolute bottom-1 w-1 h-1 rounded-full bg-white" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Editor Drawer ─────────────────────────────────────────────
function EventEditor({ event, onClose, onSaved, onDeleted }) {
  const isNew = !event?.event_id;
  const [form, setForm] = useState(() => ({
    type: event?.type || "wedding",
    name: event?.name || "",
    date: event?.date || todayISO(),
    time: event?.time || "10:00",
    ready_by: event?.ready_by || "",
    venue: event?.venue || "",
    party_size: event?.party_size ?? 0,
    lead_client: event?.lead_client || { name: "", phone: "", email: "" },
    roster: event?.roster || [],
    notes: event?.notes || "",
    inspo_link: event?.inspo_link || "",
    dress_color: event?.dress_color || "",
    allergies: event?.allergies || "",
    status: event?.status || "inquiry",
    venue_option: event?.venue_option || "travel",
    // Deposit + contract — only used on existing events; PATCHed separately
    deposit_amount: event?.deposit?.amount ?? 0,
    contract_template: event?.contract?.template || "",
  }));
  const [saving, setSaving] = useState(false);
  const [sendingContract, setSendingContract] = useState(false);
  // Live mirror of the contract status so the "Send contract" button
  // disables right after the parent refresh without waiting for re-mount.
  const contractStatus = event?.contract?.status || "draft";
  const depositStatus = event?.deposit?.status || "pending";

  const setLead = (k, v) => setForm(f => ({ ...f, lead_client: { ...f.lead_client, [k]: v } }));
  const updateRoster = (idx, k, v) => {
    const next = [...form.roster];
    next[idx] = { ...next[idx], [k]: v };
    setForm(f => ({ ...f, roster: next }));
  };
  const addRosterRow = () =>
    setForm(f => ({ ...f, roster: [...f.roster, { name: "", service: "", start_time: "", notes: "" }] }));
  const removeRosterRow = (idx) =>
    setForm(f => ({ ...f, roster: f.roster.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Event name is required"); return; }
    if (!form.date) { toast.error("Date of Event is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        party_size: parseInt(form.party_size || 0, 10) || (form.roster?.length || 0),
        deposit_amount: parseFloat(form.deposit_amount || 0) || 0,
      };
      if (isNew) {
        const res = await api.post("/events", payload);
        toast.success("Event created");
        onSaved(res.data);
      } else {
        const res = await api.put(`/events/${event.event_id}`, payload);
        toast.success("Event updated");
        onSaved(res.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const del = async () => {
    if (!event?.event_id) return;
    if (!window.confirm(`Delete "${event.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/events/${event.event_id}`);
      toast.success("Event deleted");
      onDeleted?.(event.event_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const copyShareLink = async () => {
    if (!event?.share_token) { toast.error("Save the event first to get a share link"); return; }
    const url = `${window.location.origin}/events/intake/${event.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — send it to the client");
    } catch {
      toast.error("Copy failed");
    }
  };

  const sendContract = async () => {
    if (isNew) { toast.error("Save the event first, then send the contract"); return; }
    if (!form.contract_template?.trim()) { toast.error("Contract template is empty"); return; }
    setSendingContract(true);
    try {
      // First persist any edits made to the contract template, then flag
      // it as 'sent' so the bride can see it on her intake URL.
      await api.put(`/events/${event.event_id}`, { contract_template: form.contract_template });
      const res = await api.post(`/events/${event.event_id}/contract/send`);
      toast.success("Contract sent — share the intake link with the client");
      // Reflect new contract status locally so the parent re-fetch
      // doesn't blink the buttons.
      onSaved({ ...event, contract: { ...(event.contract || {}), status: "sent", template: form.contract_template }, share_token: res.data.share_token });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send contract");
    }
    setSendingContract(false);
  };

  // Pull the org-level default template into this event. Useful for
  // events created before the master template was updated — one click
  // brings them up to date without re-creating the event.
  const resetTemplate = async () => {
    if (!window.confirm("Replace the current contract text with your org-level default template? Any unsaved edits to the template will be lost.")) return;
    try {
      const r = await api.get("/events/settings/defaults");
      setForm(f => ({ ...f, contract_template: r.data.contract_template || "" }));
      toast.success("Default template loaded — click Save to keep it");
    } catch {
      toast.error("Could not load default template");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/30 backdrop-blur-sm" data-testid="event-editor"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-[640px] h-full bg-white overflow-y-auto animate-slideInRight shadow-2xl">
        <div className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">
              {isNew ? "New event" : "Edit event"}
            </p>
            <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900 truncate" data-testid="editor-title">
              {form.name || "Untitled Event"}
            </h2>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 ml-3">
            {!isNew && (
              <button onClick={copyShareLink} data-testid="copy-share-link"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50">
                <Share2 className="w-3.5 h-3.5" /> Copy share link
              </button>
            )}
            {!isNew && (
              <button onClick={del} data-testid="delete-event"
                className="w-9 h-9 rounded-full hover:bg-rose-50 flex items-center justify-center">
                <Trash2 className="w-4 h-4 text-rose-600" />
              </button>
            )}
            <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
              <X className="w-4 h-4 text-slate-600" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Type + status */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Type</p>
            <div className="grid grid-cols-3 gap-2">
              {EVENT_TYPES.map(({ key, label, Icon }) => {
                const active = form.type === key;
                return (
                  <button key={key} type="button"
                    onClick={() => setForm(f => ({ ...f, type: key }))}
                    data-testid={`type-${key}`}
                    className={`flex flex-col items-center gap-1 py-3 rounded-2xl border text-xs font-semibold transition-all ${
                      active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                    }`}>
                    <Icon className="w-4 h-4" strokeWidth={1.75} />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Event Name *</p>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Sarah & Michael's Wedding"
                data-testid="event-name" className="rounded-xl border-slate-200" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Status</p>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                data-testid="event-status"
                className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-rose-300">
                {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Date of Event *</p>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                data-testid="event-date" className="rounded-xl border-slate-200" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Start Time</p>
              <Input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                data-testid="event-time" className="rounded-xl border-slate-200" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Ready By</p>
              <Input type="time" value={form.ready_by} onChange={e => setForm(f => ({ ...f, ready_by: e.target.value }))}
                data-testid="event-ready-by" className="rounded-xl border-slate-200" />
              <p className="text-[10px] text-slate-400 mt-1">When the entire party needs to be finished</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Venue / Address</p>
              <Input value={form.venue} onChange={e => setForm(f => ({ ...f, venue: e.target.value }))}
                placeholder="Hyatt Regency Rochester, 125 E Main St"
                data-testid="event-venue" className="rounded-xl border-slate-200" />
            </div>
          </div>

          {/* Lead client */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Lead Contact</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input value={form.lead_client.name} onChange={e => setLead("name", e.target.value)}
                placeholder="Name" data-testid="lead-name" className="rounded-xl border-slate-200" />
              <Input value={form.lead_client.phone} onChange={e => setLead("phone", e.target.value)}
                placeholder="Phone" data-testid="lead-phone" className="rounded-xl border-slate-200" />
              <Input value={form.lead_client.email} onChange={e => setLead("email", e.target.value)}
                placeholder="Email" data-testid="lead-email" className="rounded-xl border-slate-200" />
            </div>
          </div>

          {/* Roster */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Party Roster ({form.roster.length})</p>
              <button onClick={addRosterRow} data-testid="add-roster-row"
                className="text-xs inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-slate-900 text-white hover:bg-slate-800">
                <Plus className="w-3 h-3" /> Add person
              </button>
            </div>
            <div className="space-y-2">
              {form.roster.length === 0 && (
                <p className="text-xs text-slate-400 italic">No one added yet. Tap &ldquo;Add person&rdquo; to start building the roster.</p>
              )}
              {form.roster.map((row, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 bg-[#FAFAFA] rounded-xl p-2.5" data-testid={`roster-row-${idx}`}>
                  <Input value={row.name || ""} onChange={e => updateRoster(idx, "name", e.target.value)}
                    placeholder="Person name" className="col-span-4 h-9 rounded-lg border-slate-200 text-sm" />
                  <Input value={row.service || ""} onChange={e => updateRoster(idx, "service", e.target.value)}
                    placeholder="Service (Updo, Makeup, …)" className="col-span-5 h-9 rounded-lg border-slate-200 text-sm" />
                  <Input type="time" value={row.start_time || ""} onChange={e => updateRoster(idx, "start_time", e.target.value)}
                    className="col-span-2 h-9 rounded-lg border-slate-200 text-sm" />
                  <button onClick={() => removeRosterRow(idx)} data-testid={`remove-row-${idx}`}
                    className="col-span-1 rounded-lg hover:bg-rose-50 flex items-center justify-center">
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Extra */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Inspo link</p>
              <Input value={form.inspo_link} onChange={e => setForm(f => ({ ...f, inspo_link: e.target.value }))}
                placeholder="https://pinterest.com/…" data-testid="event-inspo" className="rounded-xl border-slate-200" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Dress / Theme color</p>
              <Input value={form.dress_color} onChange={e => setForm(f => ({ ...f, dress_color: e.target.value }))}
                placeholder="Ivory / blush" data-testid="event-dress" className="rounded-xl border-slate-200" />
            </div>
            <div className="md:col-span-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Allergies / Sensitivities</p>
              <Input value={form.allergies} onChange={e => setForm(f => ({ ...f, allergies: e.target.value }))}
                placeholder="(none)" data-testid="event-allergies" className="rounded-xl border-slate-200" />
            </div>
            <div className="md:col-span-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Notes</p>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value.slice(0, 2000) }))}
                rows={3} data-testid="event-notes" placeholder="Trial run details, special requests, vibe…"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300 resize-none" />
            </div>
          </div>

          {/* Contract & Deposit — only meaningful for saved events. We
              still let new-event editors see the template field so they
              can tweak before clicking Create + Send. */}
          <div className="border-t border-slate-100 pt-5 space-y-4" data-testid="event-contract-section">
            <div className="flex items-center gap-2">
              <FileSignature className="w-4 h-4 text-rose-600" />
              <h3 className="font-['Outfit'] text-base font-semibold text-slate-900">Contract &amp; Deposit</h3>
              {!isNew && (
                <span className={`ml-auto text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${
                  contractStatus === "signed" ? "bg-emerald-50 text-emerald-700"
                  : contractStatus === "sent" ? "bg-amber-50 text-amber-700"
                  : "bg-slate-100 text-slate-500"
                }`}>{contractStatus === "signed" ? "Signed" : contractStatus === "sent" ? "Awaiting signature" : "Draft"}</span>
              )}
              {!isNew && depositStatus === "paid" && (
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold bg-emerald-50 text-emerald-700">Deposit paid</span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Deposit Amount (USD)</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <Input type="number" min="0" step="1" value={form.deposit_amount}
                    onChange={e => setForm(f => ({ ...f, deposit_amount: e.target.value }))}
                    data-testid="event-deposit-amount"
                    className="pl-7 rounded-xl border-slate-200" />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  {isNew ? "Defaults from org settings" : depositStatus === "paid" ? "Paid in full" : "Bride is charged this on Stripe"}
                </p>
              </div>
              <div className="md:col-span-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
                  Venue arrangement <span className="font-normal lowercase tracking-normal text-slate-400">— $150 fee either way</span>
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: "travel", label: "Stylists travel to venue", sub: "We come to you" },
                    { v: "room_rental", label: "Private styling room rental", sub: "Party comes to salon" },
                  ].map(opt => {
                    const active = (form.venue_option || "travel") === opt.v;
                    return (
                      <button key={opt.v} type="button"
                        onClick={() => setForm(f => ({ ...f, venue_option: opt.v }))}
                        disabled={contractStatus === "signed"}
                        data-testid={`venue-option-${opt.v}`}
                        className={`text-left p-3 rounded-xl border-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                          active ? "border-rose-500 bg-rose-50/40" : "border-slate-200 bg-white hover:border-slate-400"
                        }`}>
                        <p className={`text-sm font-semibold ${active ? "text-rose-700" : "text-slate-700"}`}>{opt.label}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{opt.sub}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              {!isNew && contractStatus !== "draft" && event?.share_token && (
                <div className="md:col-span-3 bg-amber-50/50 border border-amber-100 rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1">Client signing link</p>
                  <code className="text-[11px] text-slate-700 break-all">{window.location.origin}/events/intake/{event.share_token}</code>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-start justify-between gap-3 mb-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 flex-1">
                  Contract Template
                  <span className="font-normal lowercase tracking-normal text-slate-400 ml-2">
                    Merge fields: <code className="text-slate-600">{`{{client_name}} {{event_date}} {{venue}} {{ready_by}} {{party_size}} {{salon_name}} {{deposit_amount}}`}</code>
                  </span>
                </p>
                {contractStatus !== "signed" && (
                  <button type="button" onClick={resetTemplate}
                    data-testid="reset-template-btn"
                    className="text-[10px] text-rose-600 hover:text-rose-700 font-semibold uppercase tracking-widest shrink-0">
                    Reset to default
                  </button>
                )}
              </div>
              <textarea value={form.contract_template}
                onChange={e => setForm(f => ({ ...f, contract_template: e.target.value }))}
                rows={10} data-testid="event-contract-template"
                disabled={contractStatus === "signed"}
                placeholder="Paste or write the contract terms here. Merge fields are substituted when the client opens it."
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono leading-relaxed focus:outline-none focus:border-rose-300 resize-y disabled:bg-slate-50 disabled:text-slate-500" />
            </div>

            {!isNew && contractStatus !== "signed" && (
              <Button onClick={sendContract} disabled={sendingContract || !form.contract_template?.trim()}
                data-testid="send-contract-btn"
                className="bg-rose-600 text-white rounded-full h-10 px-5 hover:bg-rose-700 disabled:opacity-50">
                <Send className="w-4 h-4 mr-1.5" />
                {sendingContract ? "Sending…" : contractStatus === "sent" ? "Re-send (saves edits)" : "Save & Send Contract"}
              </Button>
            )}
            {contractStatus === "signed" && event?.contract?.signed_by_name && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-sm">
                <p className="text-emerald-800">
                  <Check className="w-3.5 h-3.5 inline mr-1" />
                  Signed by <strong>{event.contract.signed_by_name}</strong> on{" "}
                  {new Date(event.contract.signed_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Save */}
          <div className="sticky bottom-0 -mx-6 bg-white border-t border-slate-100 px-6 py-4 flex items-center gap-3">
            <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-900 px-3 h-10">
              Cancel
            </button>
            <div className="flex-1" />
            <Button onClick={save} disabled={saving} data-testid="save-event"
              className="bg-slate-900 text-white rounded-full h-10 px-6 hover:bg-slate-800 disabled:opacity-50">
              <Check className="w-4 h-4 mr-1.5" />
              {saving ? "Saving…" : (isNew ? "Create event" : "Save changes")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────
export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | event | "new"
  const [filter, setFilter] = useState("all"); // all | inquiry | booked | completed | cancelled
  const [selectedDate, setSelectedDate] = useState(null);
  const [tenantSlug, setTenantSlug] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [list, dash, tenant] = await Promise.all([
        api.get("/events"),
        api.get("/events/dashboard/summary"),
        api.get("/tenants/me").catch(() => ({ data: null })),
      ]);
      setEvents(list.data || []);
      setDashboard(dash.data || null);
      setTenantSlug(tenant.data?.slug || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load events");
    }
    setLoading(false);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    let arr = events;
    if (filter !== "all") arr = arr.filter(e => e.status === filter);
    if (selectedDate) arr = arr.filter(e => e.date === selectedDate);
    return arr;
  }, [events, filter, selectedDate]);

  const onSaved = (saved) => {
    if (!saved) return;
    setEvents(prev => {
      const idx = prev.findIndex(e => e.event_id === saved.event_id);
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
      return [saved, ...prev];
    });
    setEditor(saved);
    fetchAll();
  };
  const onDeleted = (eid) => {
    setEvents(prev => prev.filter(e => e.event_id !== eid));
    setEditor(null);
    fetchAll();
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const counts = dashboard?.counts || {};
  const upcoming = dashboard?.upcoming || [];

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="events-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Events & Groups</h1>
          <p className="text-slate-600 text-sm mt-1">Weddings, bridal parties, proms, and large-group styling</p>
        </div>
        <div className="flex items-center gap-2">
          {tenantSlug && (
            <Button
              variant="outline"
              data-testid="share-intake-form-btn"
              onClick={async () => {
                const url = `${window.location.origin}/events/inquire/${tenantSlug}`;
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success("Intake form link copied — paste it in an email, text, or your IG bio");
                } catch {
                  toast.message(url);
                }
              }}
              className="border-slate-200 rounded-full h-10 px-4 text-sm"
              title="Copy a public link clients can fill out to request an event"
            >
              <Share2 className="w-4 h-4 mr-1.5" /> Share Intake Form
            </Button>
          )}
          <Button onClick={() => setEditor({})} data-testid="new-event-btn"
            className="bg-slate-900 text-white rounded-full h-10 px-5 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-1.5" /> New Event
          </Button>
        </div>
      </div>

      {/* Helper banner — discoverable hint for the share-intake feature */}
      {tenantSlug && (
        <div className="bg-gradient-to-r from-rose-50 via-amber-50 to-rose-50 border border-rose-100 rounded-2xl p-4 mb-6 flex items-center gap-3" data-testid="intake-helper-banner">
          <div className="w-9 h-9 rounded-xl bg-white border border-rose-200 flex items-center justify-center flex-shrink-0">
            <LinkIcon className="w-4 h-4 text-rose-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900">Send brides a blank intake form</p>
            <p className="text-xs text-slate-500 truncate">
              Anyone with the link can submit details. Each submission shows up here as a new inquiry — no account needed for the client.
            </p>
          </div>
          <code className="hidden md:inline text-[11px] text-slate-500 bg-white border border-slate-200 rounded px-2 py-1 truncate max-w-[280px]" data-testid="intake-url-preview">
            /events/inquire/{tenantSlug}
          </code>
        </div>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { Icon: CalIcon,   label: "This Month",   value: counts.events_this_month || 0, color: "#F43F5E" },
          { Icon: Users,     label: "People Booked", value: counts.people_this_month || 0, color: "#8B5CF6" },
          { Icon: Check,     label: "Booked",       value: counts.booked || 0,    color: "#10B981" },
          { Icon: Sparkles,  label: "Inquiries",    value: counts.inquiries || 0, color: "#F59E0B" },
        ].map(s => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <s.Icon className="w-5 h-5 mb-2" style={{ color: s.color }} strokeWidth={1.5} />
            <p className="font-['Outfit'] text-3xl font-medium text-slate-900">{s.value}</p>
            <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider font-semibold font-['Manrope']">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Mini calendar + Upcoming list */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <MiniCalendar events={events} onPickDate={(d) => setSelectedDate(prev => prev === d ? null : d)} selectedDate={selectedDate} />

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5" data-testid="upcoming-events">
          <div className="flex items-center justify-between mb-4">
            <p className="font-['Outfit'] text-base font-semibold text-slate-900">Upcoming events</p>
            <p className="text-xs text-slate-400">Next 5</p>
          </div>
          {upcoming.length === 0 ? (
            <div className="text-center py-8">
              <Sparkles className="w-8 h-8 text-slate-200 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No upcoming events yet</p>
              <button onClick={() => setEditor({})} className="text-xs text-rose-600 hover:underline mt-2 font-medium">Create your first event</button>
            </div>
          ) : (
            <div className="space-y-2">
              {upcoming.map(e => {
                const tm = typeMeta(e.type);
                const sm = statusMeta(e.status);
                return (
                  <button key={e.event_id} onClick={() => setEditor(e)}
                    data-testid={`upcoming-${e.event_id}`}
                    className="w-full bg-[#FAFAFA] hover:bg-slate-50 rounded-xl p-3 flex items-center gap-3 text-left transition-colors border border-transparent hover:border-slate-200">
                    <div className="w-11 h-11 rounded-xl bg-white border border-slate-200 flex flex-col items-center justify-center flex-shrink-0">
                      <p className="text-[9px] uppercase tracking-wider text-slate-400 leading-none">{new Date(e.date + "T12:00").toLocaleDateString("en-US", { month: "short" })}</p>
                      <p className="font-['Outfit'] text-lg font-semibold leading-tight">{new Date(e.date + "T12:00").getDate()}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <tm.Icon className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" />
                        <p className="text-sm font-semibold text-slate-900 truncate">{e.name}</p>
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {(e.party_size || (e.roster || []).length || 0)} people · {e.venue || "Venue TBD"} · {e.time || "Time TBD"}
                      </p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${sm.color}`}>
                      {sm.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Full list with filters */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="all-events">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <p className="font-['Outfit'] text-base font-semibold text-slate-900">All events</p>
          <div className="flex items-center gap-2 flex-wrap">
            {["all", ...STATUSES.map(s => s.key)].map(k => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                  filter === k ? "bg-slate-900 text-white" : "bg-[#F3F3F3] text-slate-600 hover:bg-[#E6E4E0]"
                }`}>
                {k === "all" ? "All" : statusMeta(k).label}
              </button>
            ))}
            {selectedDate && (
              <button onClick={() => setSelectedDate(null)} data-testid="clear-date-filter"
                className="text-xs px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 font-medium inline-flex items-center gap-1">
                <X className="w-3 h-3" /> {fmtDateShort(selectedDate)}
              </button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <Sparkles className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No events match</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(e => {
              const tm = typeMeta(e.type);
              const sm = statusMeta(e.status);
              return (
                <div key={e.event_id} onClick={() => setEditor(e)}
                  data-testid={`event-row-${e.event_id}`}
                  className="group rounded-xl border border-slate-200 hover:border-rose-200 hover:shadow-sm transition-all cursor-pointer p-4 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex flex-col items-center justify-center flex-shrink-0">
                    <p className="text-[9px] uppercase tracking-wider text-slate-400 leading-none">{new Date(e.date + "T12:00").toLocaleDateString("en-US", { month: "short" })}</p>
                    <p className="font-['Outfit'] text-lg font-semibold leading-tight">{new Date(e.date + "T12:00").getDate()}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <tm.Icon className="w-4 h-4 text-rose-500 flex-shrink-0" />
                      <p className="font-semibold text-slate-900">{e.name}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${sm.color}`}>{sm.label}</span>
                    </div>
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 mt-1">
                      <span className="inline-flex items-center gap-1"><CalIcon className="w-3 h-3" /> {fmtDateLong(e.date)}{e.time ? ` · ${e.time}` : ""}</span>
                      {e.venue && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.venue}</span>}
                      <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {(e.party_size || (e.roster || []).length || 0)} people</span>
                      {e.lead_client?.name && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {e.lead_client.name}</span>}
                    </div>
                  </div>
                  <Pencil className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editor && (
        <EventEditor
          event={editor.event_id ? editor : null}
          onClose={() => setEditor(null)}
          onSaved={onSaved}
          onDeleted={onDeleted}
        />
      )}
    </div>
  );
}
